import {
  ArrowIcon,
  CircleTickIcon,
  CircleWrongIcon,
  TimeIcon
} from "../../Assets/assets";
import { isValidArray, isValidObject } from "../../Services/validators";
import {
  alignPhoneNumber,
  capitalizeFirstLetter,
  dateAndTimeConverter,
  getUniqueDataFromObj,
  isValidDate
} from "./Constants";

export const complaintsQueryPath = {
  status: "status.currentStatus",
  source: "issuedBy.type",
  issueTakenByPhoneNumber: "takenOverBy.phoneNumber",
  issueTakenByEmail: "takenOverBy.email",
  category: "category",
  ward: "location.ward",
  zone: "location.zone",
  dateRange: "createdAt",
  PCTNo: "location.id"
};

export const complaintsTableHeadings = [
  { title: "SI.No", width: "50px", textAlign: "left" },
  { title: "PCT #", width: "260px", textAlign: "left" },
  { title: "COMPLAINT ID", width: "100px", textAlign: "left" },
  { title: "DESCRIPTION", width: "340px", textAlign: "left" },
  { title: "TURNAROUND TIME", width: "150px", textAlign: "left" },
  { title: "SOURCE", width: "180px", textAlign: "left" },
  { title: "STATUS", width: "130px", textAlign: "left" },
  { title: "RAISED AT", width: "150px", textAlign: "left" },
  { title: "UPDATED AT", width: "150px", textAlign: "right" }
];

export const defaultComplaintFilter = {
  category: null,
  dateRange: {
    type: "Today",
    from: new Date().toISOString().split("T")[0],
    to: new Date().toISOString().split("T")[0]
  },
  issueRaised: null,
  issueTakenBy: null,
  PCTNo: [],
  status: [],
  ward: [],
  zone: [],
  source: []
};

export function complaintsTableField(data, type, staging) {
  switch (data) {
    case "SI.No":
      return "serialNo";
    case "COMPLAINT ID":
      return type === "sort" && staging === "cloud" ? "__name__" : "documentId";
    case "DESCRIPTION":
      return "category";
    case "PCT #":
      return type === "sort" ? "location.PCTNo" : "locationId";
    case "TURNAROUND TIME":
      return type === "sort" ? "turnaroundOrderBy" : "turnaroundOrderBy";
    case "SOURCE":
      return type === "sort" ? "issuedBy.type" : "source";
    case "STATUS":
      return type === "sort" ? "status.currentStatus" : "currentStatus";
    case "RAISED AT":
      return "createdAt";
    case "UPDATED AT":
      return type === "sort" ? "status.updatedAt" : "updatedAt";
    default:
      return null;
  }
}

export const categoryMapping = {
  "Broken doors, latches": "உடைந்த கதவுகள், தாழ்பாள்கள்",
  "Fees are charged": "கட்டணம் வசூலிக்கப்படுகிறது",
  "Janitors are not on duty": "பராமரிப்பாளர் பணியில் இல்லை",
  "Lack of security measures": "பாதுகாப்பு குறைபாடு (பாதுகாப்புஇல்லாத கழிவறை)",
  "Lights not working": "விளக்குகள் எரியவில்லை",
  "No water supply": "தண்ணீர் வரவில்லை",
  "Restroom is not open": "கழிப்பறை திறக்கப்படவில்லை",
  "Unhygienic environment": "சுற்றுபுறம் தூய்மையாக இல்லை",
  "Unsanitary restroom": "சுத்தமில்லாத கழிவறை",
  "Waterlogged in the drainage": "கழிவு நீர் குழாயில் அடைப்பு"
};

export function convertTimestampToTimer(timestamp, complaintClosed) {
  const positiveTimestamp = Math.abs(timestamp);

  const isNegative = timestamp <= 0;

  let seconds = Math.floor(positiveTimestamp / 1000);
  let minutes = Math.floor(seconds / 60);
  let hours = Math.floor(minutes / 60);
  let days = Math.floor(hours / 24);

  seconds %= 60;
  minutes %= 60;
  hours %= 24;

  const pad = (num) => String(num).padStart(2, "0");

  const threeHours = 1000 * 60 * 60 * 3;

  return (
    <div
      className=" display-flex flex-align-items-center"
      style={{
        ...(isNegative ? { color: "#D0405A" } : {}),
        ...(!isNegative && timestamp > threeHours
          ? { color: "var(--tertiaryColor)" }
          : {}),
        ...(!isNegative &&
        !complaintClosed &&
        timestamp < threeHours &&
        0 < timestamp
          ? { color: "#D17541" }
          : {})
      }}
    >
      {complaintClosed && isNegative ? <CircleWrongIcon /> : <></>}
      {!complaintClosed && isNegative ? <TimeIcon color={"#D0405A"} /> : <></>}
      {!complaintClosed && !isNegative ? (
        <TimeIcon
          color={
            timestamp < threeHours && 0 < timestamp
              ? "#D17541"
              : "var(--tertiaryColor)"
          }
        />
      ) : (
        <></>
      )}
      {complaintClosed && !isNegative ? <CircleTickIcon /> : <></>}

      <div className=" padding-left-small">
        {complaintClosed && !isNegative
          ? "On time"
          : complaintClosed && isNegative
          ? "Over time"
          : days
          ? `${isNegative ? "-" : ""} ${pad(days)}D : ${pad(hours)}H`
          : `${isNegative ? "-" : ""} ${pad(hours)}H : ${pad(minutes)}M`}
      </div>
    </div>
  );
}

export function getRemainingTime(turnaround, closedAt) {
  return closedAt ? turnaround - closedAt : turnaround - +new Date();
}

export function getSourceTableValue(value) {
  switch (value) {
    case "independentEngineer":
      return "Independent Engineers";
    case "public":
      return "Public";
    case "procedure":
      return "Procedure";
    case "customerSupport":
      return "Customer Support";
    case "internal":
      return "Internal";

    default:
      return value;
  }
}

export const getSourceDatabaseValue = (value) => {
  switch (value) {
    case "Public":
      return "public";
    case "Procedure":
      return "procedure";
    case "Customer Support":
      return "customerSupport";
    case "Independent Engineers":
      return "independentEngineer";
    default:
      return value;
  }
};

export function convertToTableData(complaintsData) {
  const complaintTableData = [];

  for (let i = 0; i < complaintsData.length; i++) {
    const item = complaintsData[i];
    const transformedItem = {
      ...item,
      serialNo: i + 1,
      documentId: item?.documentId,
      locationId:
        `${item?.location?.PCTNo?.padStart(2, "0")} - ${
          item?.location?.address
        }` || "N/A",
      category: item?.category,
      source: (
        <div className=" display-flex flex-justify-content-space-between flex-align-items-center">
          <div>{getSourceTableValue(item?.issuedBy?.type)}</div>
          {item?.issuedBy?.linkedWith?.length ? (
            <div>
              {String.fromCodePoint(0x1f517)}{" "}
              {item?.issuedBy?.linkedWith?.length}
            </div>
          ) : (
            <></>
          )}
        </div>
      ),
      turnaroundOrderBy: item?.turnaround
        ? convertTimestampToTimer(
            getRemainingTime(
              item?.turnaround,
              item?.status?.currentStatus === "PENDING CLOSURE" ||
                item?.status?.currentStatus === "CLOSED"
                ? item?.closure?.resolvedAt
                : null
            ),
            item?.status?.currentStatus === "PENDING CLOSURE" ||
              item?.status?.currentStatus === "CLOSED"
              ? true
              : false
          )
        : "N/A",
      currentStatus: capitalizeFirstLetter(item?.status?.currentStatus),
      createdAt: item?.createdAt
        ? `${dateAndTimeConverter(
            item?.createdAt,
            "Time"
          )}, ${dateAndTimeConverter(item?.createdAt, "thirdDate")}`
        : "N/A",
      createdAtTimestamp: item?.createdAt,
      updatedAt: item?.status?.updatedAt
        ? `${dateAndTimeConverter(
            item?.status?.updatedAt,
            "Time"
          )}, ${dateAndTimeConverter(item?.status?.updatedAt, "thirdDate")}`
        : "N/A"
    };
    complaintTableData.push(transformedItem);
  }
  return complaintTableData;
}

export function getLocationList(locations) {
  if (!isValidObject(locations)) {
    return [];
  }

  return Object.values(locations)
    .sort((a, b) => {
      const nameA = parseInt(a.PCTNo) || 0;
      const nameB = parseInt(b.PCTNo) || 0;
      return nameA - nameB;
    })
    .map(({ PCTNo, location }) => `${PCTNo || "N/A"} - ${location || "N/A"}`);
}

export const complaintFilterData = (locations) => [
  {
    key: "dateRange",
    multiple: false,
    options: [
      "Lifetime",
      "Today",
      "Last 7 days",
      "Last 30 days",
      "Select Date"
    ],
    title: "DATE RANGE"
  },
  // {
  //   key: "category",
  //   multiple: true,
  //   options: Object.keys(categoryMapping).sort((a, b) => a.localeCompare(b)),
  //   title: "CATEGORY"
  // },
  // {
  //   key: "issueRaised",
  //   multiple: false,
  //   options: ["Select Employee"],
  //   title: "ISSUE RAISED BY"
  // },
  // {
  //   key: "issueTakenBy",
  //   multiple: false,
  //   options: ["Select Employee"],
  //   title: "ISSUE TAKEN BY"
  // },
  {
    key: "PCTNo",
    multiple: true,
    search: true,
    searchData: {
      label: "Search PCT / Address"
    },
    options: getLocationList(locations),
    title: "LOCATIONS"
  },
  {
    key: "status",
    multiple: true,
    options: ["Closed", "Open", "Under Review", "Pending Closure"],
    title: "STATUS"
  },
  {
    key: "ward",
    multiple: true,
    search: true,
    searchData: {
      label: "Search Ward"
    },
    options: getUniqueDataFromObj("ward", locations)?.sort((a, b) =>
      a?.localeCompare(b)
    ),
    title: "WARD"
  },
  {
    key: "zone",
    multiple: true,
    options: getUniqueDataFromObj("zone", locations)?.sort(
      (a, b) => parseInt(a) - parseInt(b)
    ),
    title: "ZONE"
  },
  {
    key: "source",
    multiple: true,
    options: [
      "Public",
      "Procedure",
      "Customer Support",
      "Independent Engineers"
    ],
    title: "SOURCE"
  }
];

export const complaintsQueryBuilder = (filterData, locationData) => {
  const query = [];

  const addQuery = (path, operator, value) => {
    query.push(path, operator, value);
  };

  const addRangeQuery = (path, from, to) => {
    addQuery(path, ">=", +new Date(`${from}:00:00:00`));
    addQuery(path, "<=", +new Date(`${to}:23:59:59`));
  };

  Object.entries(filterData).forEach(([key, value]) => {
    if (!value) {
      return;
    }
    switch (key) {
      case "dateRange":
        if (
          value.from &&
          isValidDate(value.from) &&
          value.to &&
          isValidDate(value.to)
        ) {
          addRangeQuery(complaintsQueryPath[key], value.from, value.to);
        }
        break;

      case "PCTNo":
        if (locationData && isValidArray(value)) {
          const locationIds = value
            .map(
              (data) =>
                Object.values(locationData).find(
                  (location) => location.PCTNo === data.split(" - ")[0]
                )?.documentId
            )
            .filter(Boolean);
          if (isValidArray(locationIds)) {
            addQuery(complaintsQueryPath[key], "in", locationIds);
          }
        }
        break;

      case "category": {
        if (isValidArray(value)) {
          const allCategories = [];

          value.forEach((v) => {
            if (v) {
              allCategories.push(v);
              if (categoryMapping[v]) {
                allCategories.push(categoryMapping[v]);
              }
            }
          });

          const uniqueCategories = [...new Set(allCategories)];

          addQuery(complaintsQueryPath[key], "in", uniqueCategories);
        }
        break;
      }

      case "source":
        if (isValidArray(value)) {
          addQuery(
            complaintsQueryPath[key],
            "in",
            value.map((filterValue) => getSourceDatabaseValue(filterValue))
          );
        }
        break;

      case "issueTakenBy":
        if (value) {
          const fieldKey = value?.includes("@")
            ? `${key}Email`
            : `${key}PhoneNumber`;
          addQuery(complaintsQueryPath[fieldKey], "in", [
            value?.includes("@") ? value : `+91${value}`
          ]);
        }
        break;

      case "status":
        if (isValidArray(value)) {
          addQuery(
            complaintsQueryPath[key],
            "in",
            value.map((element) => element.toUpperCase())
          );
        }
        break;

      default:
        if (isValidArray(value)) {
          addQuery(complaintsQueryPath[key], "in", value);
        }
        break;
    }
  });

  return query;
};

export const getSelectedComplaintsLatestUpdatesData = (data) => {
  const getValidCard = (status) => {
    return (
      <div
        style={{
          width: "fit-content",
          height: "fit-content",
          padding: "4px 8px",
          fontSize: "10px",
          borderRadius: "4px",
          backgroundColor:
            status === "CLOSED"
              ? "#00892F29"
              : status === "UNDER REVIEW"
              ? "rgba(246, 201, 88, 0.12)"
              : status === "PENDING CLOSURE"
              ? "rgba(46, 169, 255, 0.12)"
              : "#FF4C4C1A",
          color:
            status === "CLOSED"
              ? "#00892F"
              : status === "UNDER REVIEW"
              ? "rgba(181, 132, 0, 1)"
              : status === "PENDING CLOSURE"
              ? "rgba(0, 150, 255, 1) "
              : "#FF4C4C"
        }}
      >
        <div className="white-space-nowrap">{status}</div>
      </div>
    );
  };
  const getRefractedData = (key) => {
    const { updatedAt, updatedBy, currentStatus, previousStatus, turnaround } = data;

    switch (key) {
      case "status":
        if (previousStatus && currentStatus) {
          return (
            <div className="display-flex flex-justify-content-center flex-align-items-center width-fit-content">
              {getValidCard(previousStatus)}
              <div className="padding-left-default padding-right-default">
                {" "}
                &rarr;
              </div>
              {getValidCard(currentStatus)}
            </div>
          );
        } else if (currentStatus) {
          return (
            <div className="display-flex flex-justify-content-center flex-align-items-center width-fit-content">
              {getValidCard(currentStatus)}
            </div>
          );
        } else {
          return "N/A";
        }

      case "updatedAt":
        return updatedAt
          ? `${dateAndTimeConverter(updatedAt, "Time")}, ${dateAndTimeConverter(
              updatedAt,
              "thirdDate"
            )}`
          : "N/A";

      case "updatedBy": {
        const { employeeName, name, phoneNumber, email } = updatedBy || {};
        return `${alignPhoneNumber(phoneNumber) ||
          email ||
          "N/A"} , ${employeeName || name || "N/A"}`;
      }

      case "turnaround": {
        return `${dateAndTimeConverter(turnaround, "Time")}, ${dateAndTimeConverter(turnaround, "thirdDate")}`;
      }

      default:
        return "N/A";
    }
  };
  return [
    typeof data.currentStatus === "string"
      ? ["STATUS", getRefractedData("status")]
      : [],
    ...(data.updatedAt ? [["UPDATED AT", getRefractedData("updatedAt")]] : []),
    ...(data.updatedBy ? [["UPDATED BY", getRefractedData("updatedBy")]] : []),
    ...(data.turnaround ? [["TURNAROUND TIME", getRefractedData("turnaround")]] : [])
  ];
};

export const getSelectedComplaintsDetailsData = (data) => {
  const { issuedBy, turnaround, location } = data || {};

  const getRefractedData = (key) => {
    switch (key) {
      case "source": {
        return getSourceTableValue(issuedBy?.type) || "N/A";
      }

      case "turnaround":
        return (
          <>
            {dateAndTimeConverter(turnaround, "Time")} {", "}
            {dateAndTimeConverter(turnaround, "thirdDate")}
          </>
        );

      case "linkedWith":
        const linkedWithData = issuedBy?.linkedWith || [];
        return linkedWithData.length > 0 ? (
          <div
            className="display-flex flex-direction-column inherit-parent-width overflow-scroll"
            style={{ maxHeight: "172px" }}
          >
            {linkedWithData.map((data, index) => {
              const { userDetails, issuedAt } = data || {};
              return (
                <div
                  className={`display-flex text-uppercase ${
                    index === 0 ? "" : "padding-top-default"
                  } ${
                    index === linkedWithData.length - 1
                      ? ""
                      : "padding-bottom-default"
                  }`}
                  key={index}
                >
                  {`${alignPhoneNumber(userDetails.phoneNumber) ||
                    "N/A"}  , ${userDetails.name ||
                    "N/A"} , ${dateAndTimeConverter(
                    issuedAt,
                    "Time"
                  )} , ${dateAndTimeConverter(issuedAt, "thirdDate")}`}
                </div>
              );
            })}
          </div>
        ) : (
          "N/A"
        );

      case "location":
        return `${location?.PCTNo} - ${location?.address}` || "N/A";

      default:
        return "N/A";
    }
  };

  return [
    ["SOURCE", getRefractedData("source")],
    isValidArray(issuedBy?.linkedWith)
      ? ["LINKED WITH", getRefractedData("linkedWith")]
      : [],
    // ["LOCATION", getRefractedData("location")],
    turnaround ? ["TURNAROUND TIME", getRefractedData("turnaround")] : []
  ];
};

export const getStatusChartData = (count) => {
  const statusLabels = ["closed", "review", "open", "pending"];
  return {
    labels: statusLabels.map((label) => label.toUpperCase()),
    datasets: [
      {
        label: "Complaint status",
        data: statusLabels.map((label) => count?.[label] || 0),
        backgroundColor: [
          `${getComputedStyle(document.documentElement).getPropertyValue(
            "--secondaryColor"
          )}`,
          "#6E6E6E",
          `${getComputedStyle(document.documentElement).getPropertyValue(
            "--tertiaryColor"
          )}`,
          "#C477FF"
        ],
        borderColor: [
          `${getComputedStyle(document.documentElement).getPropertyValue(
            "--secondaryColor"
          )}`,
          "#6E6E6E",
          `${getComputedStyle(document.documentElement).getPropertyValue(
            "--tertiaryColor"
          )}`,
          "#C477FF"
        ],
        borderWidth: 2,
        borderRadius: 0,
        spacing: 2
      }
    ],
    defaultValue: {
      label: "TOTAL COMPLAINTS",
      value: statusLabels.reduce((sum, label) => sum + (count?.[label] || 0), 0)
    }
  };
};

export const getClosedDurationChartData = (count) => {
  return {
    labels: ["<1 DAY", "1-3 DAYS", "3-7 DAYS", ">7 DAYS"],
    datasets: [
      {
        label: "Dataset 1",
        backgroundColor: [
          `${getComputedStyle(document.documentElement).getPropertyValue(
            "--secondaryColor"
          )}`,
          "#6E6E6E",
          `${getComputedStyle(document.documentElement).getPropertyValue(
            "--tertiaryColor"
          )}`,
          "#D0405A"
        ],
        borderColor: [
          `${getComputedStyle(document.documentElement).getPropertyValue(
            "--secondaryColor"
          )}`,
          "#6E6E6E",
          `${getComputedStyle(document.documentElement).getPropertyValue(
            "--tertiaryColor"
          )}`,
          "#D0405A"
        ],
        data:
          isValidObject(count) &&
          Object.keys(count)
            ?.map((key) => {
              if (key !== "average") {
                return count[key];
              }
              return null;
            })
            .filter((value) => value !== null),
        borderWidth: 2,
        borderRadius: 0,
        spacing: 2
      }
    ],
    defaultValue: { label: "AVERAGE HOURS", value: count?.average || 0 }
  };
};

export const getZoneChartData = (count) => {
  if (!isValidObject(count)) {
    return {
      labels: [],
      datasets: [
        {
          label: "Complaint Zone",
          data: [],
          backgroundColor: [],
          borderColor: [],
          borderWidth: 2,
          borderRadius: 0,
          spacing: 2
        }
      ],
      defaultValue: {
        label: "TOTAL COMPLAINTS",
        value: 0
      }
    };
  }

  const backgroundColor = [];
  const borderColor = [];

  Object.values(count).forEach((_value, index) => {
    const color = generateColorMap()[index] || generateColorMap().default;
    backgroundColor.push(color.bg);
    borderColor.push(color.border);
  });

  return {
    labels: Object.keys(count),
    datasets: [
      {
        label: "Complaint Zones",
        data: Object.values(count),
        backgroundColor: backgroundColor,
        borderColor: borderColor,
        borderWidth: 2,
        borderRadius: 0,
        spacing: 2
      }
    ],
    defaultValue: {
      label: "TOTAL COMPLAINTS",
      value: Object.values(count).reduce((sum, value) => sum + value, 0)
    }
  };
};

export const getCategoryChartData = (count) => {
  if (!isValidObject(count)) {
    return {
      labels: [],
      datasets: [
        {
          label: "Complaint Categories",
          data: [],
          backgroundColor: [],
          borderColor: [],
          borderWidth: 2,
          borderRadius: 0,
          spacing: 2
        }
      ],
      defaultValue: {
        label: "TOTAL COMPLAINTS",
        value: 0
      }
    };
  }

  const backgroundColor = [];
  const borderColor = [];

  Object.values(count).forEach((_value, index) => {
    const color = generateColorMap()[index] || generateColorMap().default;
    backgroundColor.push(color.bg);
    borderColor.push(color.border);
  });

  return {
    labels: Object.keys(count),
    datasets: [
      {
        label: "Complaint Categories",
        data: Object.values(count),
        backgroundColor: backgroundColor,
        borderColor: borderColor,
        borderWidth: 2,
        borderRadius: 0,
        spacing: 2
      }
    ],
    defaultValue: {
      label: "TOTAL COMPLAINTS",
      value: Object.values(count).reduce((sum, value) => sum + value, 0)
    }
  };
};

function generateColorMap() {
  const colorMap = {
    default: {
      bg: "rgba(200, 200, 200, 1)",
      border: "rgba(200, 200, 200, 1)"
    }
  };

  colors.forEach((color, index) => {
    const borderColor = color.replace(/0\.5\)$/, "1)");
    const bgColor = color.replace(/0\.5\)$/, "1)");
    colorMap[index] = {
      bg: bgColor,
      border: borderColor
    };
  });

  return colorMap;
}

export const getRaisedChartData = (count) => {
  const labels = [
    "INTERNAL",
    "CUSTOMER SUPPORT",
    "PUBLIC",
    "INDEPENDENT ENGINEER"
  ];
  const data = [
    count?.internal || 0,
    count?.customerSupport || 0,
    count?.public || 0,
    count?.independentEngineer || 0
  ];

  return {
    labels,
    datasets: [
      {
        label: "Complaint Raised",
        data,
        backgroundColor: [
          `${getComputedStyle(document.documentElement).getPropertyValue(
            "--secondaryColor"
          )}`,
          "#6E6E6E",
          `${getComputedStyle(document.documentElement).getPropertyValue(
            "--tertiaryColor"
          )}`,
          "#6ba8a9"
        ],
        borderColor: [
          `${getComputedStyle(document.documentElement).getPropertyValue(
            "--secondaryColor"
          )}`,
          "#6E6E6E",
          `${getComputedStyle(document.documentElement).getPropertyValue(
            "--tertiaryColor"
          )}`,
          "#6ba8a9"
        ],
        borderWidth: 2,
        borderRadius: 0,
        spacing: 2
      }
    ],
    defaultValue: {
      label: "TOTAL COMPLAINTS",
      value: count?.total || 0
    }
  };
};

export const getMonthlyComparisonBarChartData = (count) => {
  return {
    labels: count?.map((data) => data.month),
    datasets: [
      {
        label: "OPEN",
        backgroundColor: "#6E6E6E",
        borderColor: "#6E6E6E",
        borderWidth: 2,
        data: count?.map((data) => data?.count?.open),
        hoverBackgroundColor: "#D0405A",
        hoverBorderColor: "#D0405A",
        barThickness: 50,
        spacing: 2
      },
      {
        label: "CLOSED",
        backgroundColor: "#6E6E6E",
        borderColor: "#6E6E6E",
        borderWidth: 2,
        hoverBackgroundColor: `${getComputedStyle(
          document.documentElement
        ).getPropertyValue("--secondaryColor")}`,
        hoverBorderColor: `${getComputedStyle(
          document.documentElement
        ).getPropertyValue("--secondaryColor")}`,
        data: count?.map((data) => data.count.closed),
        barThickness: 50,
        spacing: 2
      }
    ],
    defaultValue: {
      label: "AVERAGE HOURS",
      value: (
        count
          ?.map((data) => data?.count.total)
          ?.reduce((sum, value) => sum + value, 0) / count?.length
      )?.toFixed(0)
    }
  };
};

export const getLongestDurationBarChartData = (complaints) => {
  return {
    labels: complaints?.map((data) => data.documentId),
    datasets: [
      {
        label: "HOURS",
        backgroundColor: "rgba(217, 217, 217, 0.5)",
        borderColor: "rgba(217, 217, 217, 1)",
        borderWidth: 1,
        data: complaints?.map((data) => data.duration.hours),
        barThickness: 22
      }
    ],
    defaultValue: {
      label: "AVERAGE HOURS",
      value: (
        complaints
          ?.map((data) => data.duration.hours)
          ?.reduce((sum, value) => sum + value, 0) / complaints?.length ||
        0 / 3600000
      )?.toFixed(2)
    }
  };
};

const colors = [
  "rgba(76, 100, 255, 0.5)",
  "rgba(217, 217, 217, 0.5)",
  "rgba(255, 108, 108, 0.5)",
  "rgba(105, 155, 91, 0.5)",
  "rgba(222, 74, 155, 0.5)",
  "rgba(107, 168, 154, 0.5)",
  "rgba(183, 199, 144, 0.5)",
  "rgba(196, 119, 255, 0.5)",
  "rgba(255, 107, 107, 0.5)",
  "rgba(255, 105, 64, 0.5)"
];

export const GraphControllerButton = (props) => {
  return (
    <div
      className="display-flex flex-justify-content-space-between flex-direction-column"
      style={{
        width: "28px",
        height: "560px",
        marginLeft: "16px"
      }}
    >
      <div
        onClick={props.decrement}
        style={{
          height: "45%"
        }}
        className={`display-flex flex-justify-content-center flex-align-items-center ${
          props.selected === "secondary"
            ? "next-hover-effect cursor-pointer"
            : "half-opacity cursor-no-drop"
        }`}
      >
        <div
          style={{
            height: "fit-content",
            width: "fit-content",
            transform: "rotate(180deg)"
          }}
        >
          <ArrowIcon color="var(--tertiaryColor6)" />
        </div>
      </div>
      <div className="display-flex flex-justify-content-center flex-align-items-center flex-direction-column">
        <div
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: props.selected === "initial" ? "white" : "#3E3E3E"
          }}
        />
        <div
          style={{
            padding: "4px"
          }}
        />
        <div
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor:
              props.selected === "secondary" ? "white" : "#3E3E3E"
          }}
        />
      </div>
      <div
        onClick={props.increment}
        className={`display-flex flex-justify-content-center flex-align-items-center ${
          props.selected === "initial"
            ? "next-hover-effect cursor-pointer"
            : "half-opacity cursor-no-drop"
        }`}
        style={{
          height: "45%",
          width: "100%"
        }}
      >
        <div
          style={{
            height: "fit-content",
            width: "fit-content"
          }}
        >
          <ArrowIcon color="var(--tertiaryColor6)" />
        </div>
      </div>
    </div>
  );
};
